/**
 * @fileoverview Component for managing and deleting sessions that occur on holidays.
 * This page allows authorized users to view holidays and delete sessions that
 * meet specific occurrence criteria.
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  Loading,
  Menu,
  CardNotification,
  Text,
  Box,
  TextField,
  Button,
  toast,
  Checkbox,
} from "@foris/avocado-suite";
import DELETE_SESSIONS_BY_HOLIDAY from "../../graphql/deleteSessionsByHolidays.mutation.graphql";
import { useGetHolidays } from "@services/collections/holidays/holidaysServices";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Header } from "@common/components";
import Layout from "@common/layout/Layout";
import Modal from "@components/modal";
import { PathParams } from "@edhtypes/general";
import { ContextEDH } from "@context/ContextEDH";
import scss from "./sessionsDelete.module.scss";

/**
 * SessionsDelete component - Handles the UI and logic for deleting sessions that occur on holidays.
 * Features:
 * - Permission-based access control
 * - Displays list of holidays
 * - Configurable session deletion threshold
 * - Confirmation workflow with modal
 * - Success notification
 */
const SessionsDelete = () => {
  const { state } = useContext(ContextEDH);
  const { scenario, origin, workspace }: PathParams = useParams();
  const history = useHistory();

  const [sessionsCountEnabled, setSessionsCountEnabled] = useState(true);
  const [sessionCount, setSessionCount] = useState("7"); // Default threshold for session deletion
  const [sessionsWithoutScheduleEnabled, setSessionsWithoutScheduleEnabled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { getDeletedSessionsByHolidayReportUrl, adaptedDarwinUrl, homeUrl, adaptedHomeUrl } =
    useNavigationUrl();

  const { t } = useTranslation();

  const [deleteSessions, { loading: deletingSessions }] = useMutation(DELETE_SESSIONS_BY_HOLIDAY);

  // Redirect unauthorized users to home
  if (!state?.base?.base?.user?.abilities?.can_delete_sessions_in_holidays) {
    history.replace(homeUrl(workspace, scenario, origin));
  }

  const [{ holidays, isLoading }, getHolidays] = useGetHolidays({
    scenario,
  });

  const isFormValid = useMemo(() => {
    if (sessionsCountEnabled) {
      return sessionCount && Number(sessionCount) > 0;
    }

    return true;
  }, [sessionsCountEnabled, sessionCount]);

  /**
   * Handles the session deletion process
   * - Validates form input
   * - Executes GraphQL mutation to delete sessions
   * - Shows success notification when complete
   */
  const handleDeleteSessions = async () => {
    if (!isFormValid) {
      return;
    }

    setShowConfirmModal(false);
    const response = await deleteSessions({
      variables: {
        scenarioId: scenario,
        originId: origin,
        input: {
          dryRun: false,
          skipValidations: false,
          minOccurrencesToDelete: sessionsCountEnabled ? Number(sessionCount) : undefined,
          removeSessionsWithoutSchedules: sessionsWithoutScheduleEnabled,
        },
      },
    });

    if (!!response?.data?.cube?.sessionsByHoliday) {
      toast.success(t("remove-session-holidays.snackbar.title"), {
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    if (!isLoading && !Object.keys(holidays).length) {
      getHolidays();
    }
  }, []);

  /**
   * Transform holidays data for display
   * - Converts dates from ISO format (YYYY-MM-DD) to localized format (DD-MM-YYYY)
   * - Memoized to prevent unnecessary recalculations
   */
  const holidaysArr = useMemo(
    () =>
      Object.values(holidays)?.map(holiday => {
        const [year, month, day] = holiday?.date.split("-");

        return {
          ...holiday,
          date: `${day}-${month}-${year}`,
        };
      }),
    [holidays],
  );

  /**
   * Navigation handler for cancellation/back action
   * - Uses browser history when possible
   * - Falls back to direct navigation to home page
   */
  const handleBackToHome = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push(homeUrl(workspace, scenario, origin));
    }
  };

  const isHolidayVisible = holidaysArr.length > 0;
  const holidaysList = isHolidayVisible ? (
    holidaysArr.map(holiday => (
      <Text className={scss.item} type="sm" key={holiday?.id}>
        {holiday?.date} - {holiday?.name}
      </Text>
    ))
  ) : (
    <Text type="sm">-</Text>
  );

  return (
    <Layout contextSearch>
      {(isLoading || deletingSessions) && <Loading />}

      <Header
        className={scss.header}
        titleClassName={scss.header_title}
        title={t("remove-session-holidays.title")}
        breadcrumbItems={[
          {
            title: t("remove-session-holidays.breadcrumb.previous"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("remove-session-holidays.breadcrumb.current"),
          },
        ]}
        renderMenu={() =>
          !!adaptedDarwinUrl && (
            <Menu
              className={scss.header_menuItem}
              listOrigin="right"
              items={[
                {
                  label: t("remove-session-holidays.menu-btn"),
                  icon: "download",
                  onClick: () => {
                    toast.success(t("remove-session-holidays.snackbar.download-report"), {
                      autoClose: 5000,
                    });

                    window.open(getDeletedSessionsByHolidayReportUrl(), "_blank");
                  },
                },
              ]}
            />
          )
        }
      />

      <main className={scss.sessionsDelete}>
        <CardNotification
          state="info"
          title={t("remove-session-holidays.info-notification.title")}
          outlined
        >
          <Text type="sm">{t("remove-session-holidays.info-notification.description")}</Text>
        </CardNotification>
        <Box className={scss.sessionsDelete_containerInfo}>
          <Text weight="medium" type="lg">
            {t("remove-session-holidays.logs.title")}
          </Text>

          <div className={scss.logs}>
            <ul className={scss.logs_list}>{holidaysList}</ul>
          </div>
        </Box>
        <div className={scss.sessionsDelete_exceptions}>
          <Text className={scss.formTitle} weight="medium" type="lg">
            {t("remove-session-holidays.exceptions.title")}
          </Text>
          <Checkbox
            className={scss.checkboxField}
            checked={sessionsCountEnabled}
            onChange={() => setSessionsCountEnabled(!sessionsCountEnabled)}
            labelRight={
              <div className={scss.form}>
                <Text type="sm">{t("remove-session-holidays.exceptions.form.label")}</Text>
                <TextField
                  type="number"
                  min={1}
                  value={sessionCount}
                  className={scss.form_textField}
                  onChange={e => setSessionCount(e?.target?.value)}
                />
                <Text type="sm">{t("remove-session-holidays.exceptions.form.suffix")}</Text>
              </div>
            }
          />
          <Checkbox
            className={scss.checkboxField}
            checked={sessionsWithoutScheduleEnabled}
            onChange={() => setSessionsWithoutScheduleEnabled(!sessionsWithoutScheduleEnabled)}
            labelRight={t("remove-session-holidays.exceptions.sessions-without-schedule")}
          />
        </div>

        <div className={scss.sessionsDelete_actions}>
          <Button onClick={handleBackToHome} variant="secondary">
            {t("remove-session-holidays.btn-cancel")}
          </Button>
          <Button onClick={() => setShowConfirmModal(true)} disabled={!isFormValid}>
            {t("remove-session-holidays.btn-remove")}
          </Button>
        </div>
      </main>

      <Modal
        className={scss.modal}
        show={showConfirmModal}
        colorButtonPrincipal="danger"
        typeState="confirm"
        title={t("remove-session-holidays.modal.title")}
        textButtonPrincipal={t("remove-session-holidays.modal.btn-remove")}
        textButtonSecondary={t("remove-session-holidays.modal.btn-cancel")}
        onClickPrincipal={handleDeleteSessions}
        onClose={() => setShowConfirmModal(false)}
        onClickSecondary={() => setShowConfirmModal(false)}
      >
        <Text>{t("remove-session-holidays.modal.text")}</Text>
        <Text>{t("remove-session-holidays.modal.text-2")}</Text>
      </Modal>
    </Layout>
  );
};

export default SessionsDelete;
